// Gráfico de barras horizontales agrupando hectáreas por fase de cosecha:
//   - Primeras (PC) ← estadios DPC, FPC, SPC
//   - Segundas (RC) ← estadios DRC, SRC
//   - Barbecho (BAR)
//
// Cada barra muestra segmentos coloreados por estadio.
// Fuente: emp_bloques (inventario activo de la finca).

import React, { useEffect, useState, useCallback } from 'react';
import { Leaf, Loader2 } from 'lucide-react';
import { supabase } from '../../supabase';
import { reclasificarEstadio } from './reclasificarEstadio';

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Bloque {
  estadio_vfp: string | null;
  area_ha: number | null;
  fecha_segunda?: string | null;
}

// Mapeo estadio → fase de cosecha.
// SOPORTE agrupa semilleros (SPC, SRC) y barbecho (BAR) porque no son fruta viva
// en producción — son área de respaldo / preparación.
// FRC (Post Forza RC) cae en RC junto con DRC.
type Fase = 'PC' | 'RC' | 'SOPORTE';
const FASE_DE_ESTADIO: Record<string, Fase> = {
  DPC: 'PC',
  FPC: 'PC',
  DRC: 'RC',
  FRC: 'RC',
  SPC: 'SOPORTE',
  SRC: 'SOPORTE',
  BAR: 'SOPORTE',
};

const FASE_LABEL: Record<Fase, string> = {
  PC:      'Primeras (PC)',
  RC:      'Segundas (RC)',
  SOPORTE: 'Semilleros + Barbecho',
};

const FASE_COLOR_BORDE: Record<Fase, string> = {
  PC:      '#22c55e',
  RC:      '#3b82f6',
  SOPORTE: '#94a3b8',
};

const ESTADIO_LABEL: Record<string, string> = {
  DPC: 'Desarrollo PC',
  FPC: 'Post Forza PC',
  SPC: 'Semillero PC',
  DRC: 'Desarrollo RC',
  FRC: 'Post Forza RC',
  SRC: 'Semillero RC',
  BAR: 'Barbecho',
};

const ESTADIO_COLOR: Record<string, string> = {
  DPC: '#22c55e',
  FPC: '#16a34a',
  SPC: '#86efac',
  DRC: '#3b82f6',
  FRC: '#1d4ed8',
  SRC: '#60a5fa',
  BAR: '#94a3b8',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtN(n: number, dec = 2): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// ── Componente ───────────────────────────────────────────────────────────────

export function BarsFaseCosecha({ empresaId }: { empresaId: number }) {
  const [loading, setLoading] = useState(true);
  const [segmentos, setSegmentos] = useState<{
    fase: Fase;
    estadios: { codigo: string; nombre: string; area: number; color: string }[];
    total: number;
  }[]>([]);
  const [totalGlobal, setTotalGlobal] = useState(0);

  const cargar = useCallback(async () => {
    setLoading(true);
    // Paginar por si hay >1000 bloques
    const PAGE = 1000;
    const bloques: Bloque[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const { data, error } = await supabase
        .from('emp_bloques')
        .select('estadio_vfp, area_ha, fecha_segunda')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .range(offset, offset + PAGE - 1);
      if (error) { setLoading(false); return; }
      const batch = data ?? [];
      bloques.push(...(batch as Bloque[]));
      if (batch.length < PAGE) break;
    }

    // Agrupar por fase → estadio
    const acumFase: Record<Fase, Record<string, number>> = {
      PC: {}, RC: {}, SOPORTE: {},
    };
    let total = 0;
    for (const b of bloques) {
      // Aplicar reclasificación FPC → FRC si han pasado >=24 semanas
      const cod = reclasificarEstadio(b);
      const fase = FASE_DE_ESTADIO[cod];
      if (!fase) continue;        // estadios desconocidos se ignoran
      const area = Number(b.area_ha ?? 0);
      acumFase[fase][cod] = (acumFase[fase][cod] ?? 0) + area;
      total += area;
    }

    const segs = (['PC', 'RC', 'SOPORTE'] as const).map(fase => {
      const estadios = Object.entries(acumFase[fase])
        .map(([codigo, area]) => ({
          codigo,
          nombre: ESTADIO_LABEL[codigo] || codigo,
          area,
          color: ESTADIO_COLOR[codigo] || '#64748b',
        }))
        .filter(e => e.area > 0)
        .sort((a, b) => b.area - a.area);
      const totalFase = estadios.reduce((s, e) => s + e.area, 0);
      return { fase, estadios, total: totalFase };
    }).filter(s => s.total > 0);

    setSegmentos(segs);
    setTotalGlobal(total);
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Para escalar las barras al mismo eje: usar la fase más grande
  const maxFase = Math.max(1, ...segmentos.map(s => s.total));
  const totalFrutaViva = segmentos
    .filter(s => s.fase === 'PC' || s.fase === 'RC')
    .reduce((acc, s) => acc + s.total, 0);

  return (
    <div style={{
      padding: '14px 18px', borderRadius: '12px',
      background: 'var(--surface-raised)', border: '1px solid var(--line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <Leaf size={15} style={{ color: '#4ade80' }} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc' }}>
          Hectáreas por fase de cosecha
        </span>
        {totalGlobal > 0 && (
          <div style={{ display: 'flex', gap: '14px', marginLeft: 'auto', fontSize: '11px', color: 'var(--ink-faint)' }}>
            <span>
              Fruta viva: <span style={{ color: '#4ade80', fontWeight: 700, fontFamily: 'monospace' }}>{fmtN(totalFrutaViva, 2)} ha</span>
            </span>
            <span>
              Total: <span style={{ color: 'var(--ink)', fontWeight: 700, fontFamily: 'monospace' }}>{fmtN(totalGlobal, 2)} ha</span>
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 gap-2" style={{ color: 'var(--ink-faint)' }}>
          <Loader2 size={14} className="animate-spin" /><span className="text-xs">Cargando…</span>
        </div>
      ) : segmentos.length === 0 ? (
        <p style={{ fontSize: '12px', color: 'var(--ink-faint)', textAlign: 'center', padding: '12px 0' }}>
          Sin bloques con estadio definido
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {segmentos.map(s => {
            const widthPct = (s.total / maxFase) * 100;
            const pctTotal = totalGlobal > 0 ? (s.total / totalGlobal) * 100 : 0;
            return (
              <div key={s.fase}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                    <span style={{ width: 8, height: 14, borderRadius: 2, background: FASE_COLOR_BORDE[s.fase], flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>
                      {FASE_LABEL[s.fase]}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--ink-faint)' }}>
                      {pctTotal.toFixed(1)}%
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: FASE_COLOR_BORDE[s.fase], fontFamily: 'monospace' }}>
                    {fmtN(s.total, 2)} ha
                  </span>
                </div>
                {/* Barra horizontal con segmentos por estadio */}
                <div style={{
                  position: 'relative', display: 'flex',
                  width: `${widthPct}%`, height: '22px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '4px', overflow: 'hidden',
                  transition: 'width 0.3s ease',
                }}>
                  {s.estadios.map(e => {
                    const segPct = (e.area / s.total) * 100;
                    return (
                      <div key={e.codigo}
                        title={`${e.nombre}: ${fmtN(e.area, 2)} ha`}
                        style={{
                          width: `${segPct}%`,
                          background: e.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '10px', fontWeight: 700, color: '#0d1117',
                          overflow: 'hidden', whiteSpace: 'nowrap',
                          cursor: 'help',
                        }}>
                        {segPct > 10 ? `${segPct.toFixed(0)}%` : ''}
                      </div>
                    );
                  })}
                </div>
                {/* Leyenda compacta de estadios bajo la barra */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '5px' }}>
                  {s.estadios.map(e => (
                    <div key={e.codigo} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: e.color, flexShrink: 0 }} />
                      <span style={{ color: 'var(--ink-muted)' }}>{e.nombre}</span>
                      <span style={{ color: 'var(--ink-faint)', fontFamily: 'monospace' }}>
                        {fmtN(e.area, 2)} ha
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BarsFaseCosecha;
